/**
 * The courses a learner actually has, grouped by where each one stands.
 *
 * The learner's Courses tab used to show only the numbers — assigned,
 * completed, average score — with no way to see WHICH courses those were
 * (Aleix, 2026-09-15: "we just see data, not the content"). The counts were
 * computed by walking these very assignments; this shows them.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';

export interface LearnerCourseRow {
  assignment_id: string;
  course_id: string;
  title: string;
  status: 'completed' | 'in_progress' | 'not_started';
  modules_total: number;
  modules_completed: number;
  progress_percent: number;
  due_date: string | null;
  completed_at: string | null;
  from_program: boolean;
  /** When this learner was last nudged about this course, if ever. */
  last_reminded_at?: string | null;
}

const GROUPS: {
  status: LearnerCourseRow['status'];
  label: string;
  tone: string;
}[] = [
  { status: 'in_progress', label: 'In progress', tone: 'bg-warning-500' },
  { status: 'not_started', label: 'Not started', tone: 'bg-text-tertiary' },
  { status: 'completed', label: 'Completed', tone: 'bg-success-500' },
];

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;

/** Days from today until `iso`; negative when it has passed. */
const daysUntil = (iso: string | null): number | null => {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
};

const CourseRow: React.FC<{
  row: LearnerCourseRow;
  onRemind?: (row: LearnerCourseRow) => void;
}> = ({ row, onRemind }) => {
  const due = daysUntil(row.due_date);
  const overdue = row.status !== 'completed' && due !== null && due < 0;
  // A nudge only makes sense for work still outstanding.
  const remindable = onRemind && row.status !== 'completed';
  const remindedAgo = row.last_reminded_at
    ? Math.floor((Date.now() - new Date(row.last_reminded_at).getTime()) / 3_600_000)
    : null;
  const recentlyReminded = remindedAgo !== null && remindedAgo < 12;

  return (
    <li className="flex items-center gap-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/courses/${row.course_id}/edit`}
            className="truncate text-sm font-semibold text-text-primary hover:underline focus-ring rounded-sm"
          >
            {row.title}
          </Link>
          {row.from_program && (
            <span className="shrink-0 rounded-full bg-primary-500/10 px-2 py-0.5 text-[11px] font-bold text-primary-600 dark:text-primary-350">
              Program
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-text-secondary">
          {row.status === 'completed' && row.completed_at
            ? `Completed ${formatDate(row.completed_at)}`
            : row.modules_total > 0
              ? `${row.modules_completed} of ${row.modules_total} modules`
              : 'No content yet'}
          {row.due_date && row.status !== 'completed' && (
            <>
              {' · '}
              <span className={overdue ? 'font-semibold text-error-600 dark:text-error-400' : ''}>
                {overdue ? `Overdue since ${formatDate(row.due_date)}` : `Due ${formatDate(row.due_date)}`}
              </span>
            </>
          )}
        </p>
      </div>

      <div className="flex w-28 shrink-0 items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-text-tertiary/15">
          <div
            className={cn(
              'h-full rounded-full',
              row.status === 'completed' ? 'bg-success-500' : 'bg-primary-500'
            )}
            style={{ width: `${Math.min(100, Math.max(0, row.progress_percent))}%` }}
          />
        </div>
        <span className="w-9 text-right text-xs tabular-nums text-text-secondary">
          {row.progress_percent}%
        </span>
      </div>

      {remindable && (
        <button
          type="button"
          onClick={() => onRemind(row)}
          disabled={recentlyReminded}
          data-testid={`remind-btn-${row.assignment_id}`}
          title={
            recentlyReminded
              ? `Reminded ${remindedAgo === 0 ? 'less than an hour' : `${remindedAgo}h`} ago`
              : `Remind about ${row.title}`
          }
          className={cn(
            'shrink-0 rounded-full border px-3 py-1.5 text-xs font-bold',
            'transition-[background-color,transform] duration-150 focus-ring',
            'active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45',
            overdue
              ? 'border-error-500/40 text-error-600 dark:text-error-400 hover:bg-error-500/[0.08]'
              : 'border-primary-500/40 text-primary-600 dark:text-primary-400 hover:bg-primary-500/[0.08]'
          )}
        >
          {recentlyReminded ? 'Reminded' : 'Remind'}
        </button>
      )}
    </li>
  );
};

const LearnerCourseRoster: React.FC<{
  courses: LearnerCourseRow[];
  /** Names which number the reader clicked to get here. */
  heading?: string;
  /** Omitted, rows carry no nudge — the roster is then read-only. */
  onRemind?: (row: LearnerCourseRow) => void;
}> = ({ courses, heading = 'Courses', onRemind }) => {
  if (courses.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-text-secondary">
        Nothing to show here yet.
      </div>
    );
  }

  return (
    <div className="card p-6" data-testid="learner-course-roster">
      <h3 className="mb-4 text-sm font-semibold text-text-primary">
        {heading} ({courses.length})
      </h3>

      <div className="space-y-6">
        {GROUPS.map(({ status, label, tone }) => {
          const rows = courses.filter((c) => c.status === status);
          if (rows.length === 0) return null;
          return (
            <section key={status}>
              <h4 className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-text-tertiary">
                <span className={cn('h-2 w-2 rounded-full', tone)} aria-hidden="true" />
                {label} ({rows.length})
              </h4>
              <ul className="divide-y divide-border-primary/10">
                {rows.map((row) => (
                  <CourseRow key={row.assignment_id} row={row} onRemind={onRemind} />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default LearnerCourseRoster;
